import { apiService } from "api/apiService";
import type {
  ConversationRowResource,
  ConversationInvitationResource,
  ConversationResource,
  MyLessongroupsConversationsCounts200,
} from "api/generated";
import { mapPaginatedResponse } from "api/mappers/pagination.mapper";
import type { PaginatedResponse, PaginatedResult } from "api/types";
import {
  classroomConversationFormDataToStoreDto,
  classroomConversationInvitationFormDataToStoreDto,
  conversationRowFromDto,
  conversationFromDto,
  conversationInvitationFromDto,
} from "conversations/api/mappers";
import type {
  ClassroomConversationInvitationFormData,
  ConversationRow,
  Conversation,
  ConversationFormData,
  ConversationInvitation,
  ConversationTabCounts,
} from "conversations/types";

/** The route param is the schedule id: the schedule decides both subject and students. */
const basePath = (schedule: string): string =>
  `api/my-lessongroups/${encodeURIComponent(schedule)}/conversations`;

export const fetchLessonClassConversations = async (
  schedule: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<PaginatedResult<ConversationRow>> => {
  const { data } = await apiService.get<
    PaginatedResponse<ConversationRowResource>
  >(basePath(schedule), { params, signal });

  return mapPaginatedResponse(data, conversationRowFromDto);
};

export const fetchLessonClassConversationCounts = async (
  schedule: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<ConversationTabCounts> => {
  const { data } = await apiService.get<MyLessongroupsConversationsCounts200>(
    `${basePath(schedule)}/counts`,
    { params, signal },
  );

  return data;
};

/**
 * The gespreksverslag endpoint of this lesgroep. The schedule stamps its own subject and
 * checks the student against its own roster, never against the lesson class name that the
 * group's other subjects share — MEETING-CLASSROOM-CONVERSATION-SUBJECT-LOCK-001.
 */
export const createLessonClassConversation = async (
  schedule: string,
  formData: ConversationFormData,
): Promise<Conversation> => {
  const { data } = await apiService.post<ConversationResource>(
    basePath(schedule),
    classroomConversationFormDataToStoreDto(formData),
  );

  return conversationFromDto(data);
};

export const createLessonClassConversationInvitation = async (
  schedule: string,
  formData: ClassroomConversationInvitationFormData,
): Promise<ConversationInvitation> => {
  const { data } = await apiService.post<ConversationInvitationResource>(
    `api/my-lessongroups/${encodeURIComponent(schedule)}/conversation-invitations`,
    classroomConversationInvitationFormDataToStoreDto(formData),
  );

  return conversationInvitationFromDto(data);
};
